/**
 * Currency-aware free gift / cart progress logic.
 *
 * The merchant configures thresholds (free_shipping, free_gift, min_cart_value)
 * as NUMBERS in the shop's primary currency (e.g., 150 = $150 USD).
 *
 * Shopify Markets serves cart.total_price in the CUSTOMER'S active currency,
 * already expressed in that currency's smallest unit:
 *   - USD/EUR/GBP -> cents      (cart.total_price = 15000 means $150.00)
 *   - JPY/KRW/etc -> whole unit (cart.total_price = 17700 means ¥17,700)
 *
 * The buggy code did `cart.total_price / 100` everywhere, which:
 *   - Treats ¥17,700 as if it were $177 -> falsely unlocks the gift.
 *   - Causes checkout to charge full price because the Shopify discount rule
 *     evaluates the threshold in the shop's currency and the JPY cart is
 *     actually ~$118 USD.
 *
 * This helper exposes the active currency rate so the rest of the theme can
 * convert a shop-currency threshold into the customer's smallest unit and
 * compare apples to apples.
 */
(function () {
  // Currencies that visually render with 0 decimal places. We use this only
  // for FORMATTING — internally Shopify always stores money as `amount × 100`,
  // even for these currencies (so ¥17,700 is stored as 1,770,000 in
  // cart.total_price and rendered by the `money` filter as ¥17,700).
  const ZERO_DECIMAL = new Set([
    'BIF','CLP','DJF','GNF','JPY','KMF','KRW','MGA','PYG',
    'RWF','UGX','VND','VUV','XAF','XOF','XPF',
  ]);

  function currencyInfo() {
    const c = (window.Shopify && window.Shopify.currency) || {};
    const active = c.active || 'USD';
    const rate = parseFloat(c.rate) || 1; // shop -> customer
    return { active, rate };
  }

  /**
   * Round a customer-currency decimal value UP to a "nice" number using a
   * step that's roughly 10% of the value's magnitude. Examples:
   *   79.16  -> 80          (step 1)
   *   131.94 -> 140         (step 10)
   *   14,582 -> 15,000      (step 1,000)
   *   24,303 -> 25,000      (step 1,000)
   *
   * Rounding UP (not down) is critical: it guarantees the threshold we
   * SHOW the customer is also the threshold we COMPARE against, so the
   * "Free gift unlocked" state is never reached before Shopify's
   * underlying automatic discount actually applies.
   */
  function roundUpToNiceDecimal(decimal) {
    if (decimal == null || isNaN(decimal) || decimal <= 0) return decimal;
    const exp = Math.floor(Math.log10(decimal));
    // For two-digit values (10–99) we always want a step of 10 so things
    // like 87.96 round up to 90 rather than 88. For larger values we use
    // ~10% of the magnitude. Below 10 we just round to whole units.
    let step;
    if (exp <= 0) step = 1;
    else if (exp === 1) step = 10;
    else step = Math.pow(10, exp - 1);
    return Math.ceil(decimal / step) * step;
  }

  /**
   * Convert a numeric amount in the SHOP'S currency into the customer's
   * Shopify-stored "cents" value (always × 100 regardless of currency),
   * so it can be compared to cart.total_price directly.
   *
   * The result is rounded UP to a nice number in the customer's currency
   * so €131.94 becomes €140 (14,000) rather than 13,194.
   *
   * Example: $150 USD shop in JPY (rate 162.04) gives raw ¥24,306; we
   * round up to ¥25,000 and return 2,500,000.
   */
  function shopAmountToCustomerCents(shopAmount) {
    if (shopAmount == null || isNaN(shopAmount)) return 0;
    const { rate } = currencyInfo();
    const rawDecimal = shopAmount * rate; // e.g. 131.94 EUR
    const niceDecimal = roundUpToNiceDecimal(rawDecimal); // e.g. 140
    return niceDecimal * 100; // Shopify cart.total_price units
  }

  /**
   * Per-currency goal overrides.
   *
   * By default both goals are the shop-currency amounts converted at the live
   * FX rate and rounded up, which drifts as the rate moves and does not
   * necessarily match what the merchant actually configured for that market.
   * Where a market has its own agreed thresholds, list them here as plain
   * amounts in that currency and they are used verbatim instead.
   */
  const CURRENCY_GOAL_OVERRIDES = {
    AUD: { shipping: 160, gift: 220 },
  };

  /** Free-shipping goal in the customer's cents, honouring any override. */
  function shippingGoalCents(shopAmount) {
    const o = CURRENCY_GOAL_OVERRIDES[currencyInfo().active];
    if (o && typeof o.shipping === 'number') return o.shipping * 100;
    return shopAmountToCustomerCents(shopAmount);
  }

  /** Free-gift goal in the customer's cents, honouring any override. */
  function giftGoalCents(shopAmount) {
    const o = CURRENCY_GOAL_OVERRIDES[currencyInfo().active];
    if (o && typeof o.gift === 'number') return o.gift * 100;
    const cfg = window.CART_FREE_GIFT_CONFIG || {};
    const amount = shopAmount == null ? cfg.freeGiftGoal : shopAmount;
    return shopAmountToCustomerCents(parseFloat(amount) || 0);
  }

  /**
   * Convert Shopify "cents" (cart.total_price scale) back to a decimal value
   * in the customer's currency, for display purposes.
   */
  function customerCentsToDecimal(cents) {
    return cents / 100;
  }

  /**
   * Format an amount in the customer's currency for display.
   * Currencies in ZERO_DECIMAL are rendered without fractional digits.
   */
  function formatCustomerMoney(decimalAmount) {
    const { active } = currencyInfo();
    const fractionDigits = ZERO_DECIMAL.has(active) ? 0 : 2;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: active,
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
      }).format(decimalAmount);
    } catch (e) {
      const symbol = (window.Shopify && window.Shopify.currency && window.Shopify.currency.symbol) || '';
      return symbol + decimalAmount.toFixed(fractionDigits);
    }
  }

  /**
   * Refresh the visual state of the cart progress bar and the
   * "choose your free gift" wrapper using the active currency.
   * Reads cart total from the .cart-progress-bar data attribute when present
   * (server-rendered with cart.total_price) so we don't need an extra fetch.
   */
  function refreshCartFreeGift(cartOverride) {
    const cfg = (window.CART_FREE_GIFT_CONFIG || {});
    const goal1 = parseFloat(cfg.freeShippingGoal) || 0;
    const goal2 = parseFloat(cfg.freeGiftGoal) || 0;

    const bar = document.querySelector('.cart-progress-bar');
    let cartCents = null;
    if (cartOverride && typeof cartOverride.total_price === 'number') {
      cartCents = cartOverride.total_price;
    } else if (bar && bar.dataset.cartTotalCents) {
      cartCents = parseInt(bar.dataset.cartTotalCents, 10);
    }
    if (cartCents == null || isNaN(cartCents)) return;

    const goal1Cents = shippingGoalCents(goal1);
    const goal2Cents = giftGoalCents(goal2);

    // ----- Progress bar widths -----
    if (bar) {
      const fill1 = goal1Cents > 0 ? Math.min(100, (cartCents / goal1Cents) * 100) : 0;
      let fill2 = 0;
      if (cartCents > goal1Cents && goal2Cents > goal1Cents) {
        fill2 = Math.min(100, ((cartCents - goal1Cents) / (goal2Cents - goal1Cents)) * 100);
      }
      const f1 = bar.querySelector('[data-fill="1"]');
      const f2 = bar.querySelector('[data-fill="2"]');
      if (f1) f1.style.width = fill1 + '%';
      if (f2) f2.style.width = fill2 + '%';

      // ----- Goal icons (checkmark vs pending circle) -----
      const checkSvg =
        '<svg class="goal-completed-icon" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">' +
        '<circle cx="11" cy="11" r="10" fill="#020202" stroke="white" stroke-width="2"/>' +
        '<path fill-rule="evenodd" clip-rule="evenodd" d="M9.57539 12.6248L14.4815 7.71875L15.375 8.6123L9.57539 14.4119L6.625 11.4447L7.51855 10.5511L9.57539 12.6248Z" fill="white" stroke="white" stroke-width="0.5"/>' +
        '</svg>';
      const pendingSvg =
        '<svg class="goal-completed-pending" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">' +
        '<circle cx="11" cy="11" r="10" fill="#F0F0F0" stroke="white" stroke-width="2"/>' +
        '</svg>';
      const icon1 = bar.querySelector('[data-goal-icon="1"]');
      const icon2 = bar.querySelector('[data-goal-icon="2"]');
      if (icon1) {
        const svgEl = icon1.querySelector('svg');
        if (svgEl) svgEl.outerHTML = cartCents >= goal1Cents ? checkSvg : pendingSvg;
      }
      if (icon2) {
        const svgEl = icon2.querySelector('svg');
        if (svgEl) svgEl.outerHTML = cartCents >= goal2Cents ? checkSvg : pendingSvg;
      }

      // ----- Top progress text -----
      const text = bar.querySelector('[data-progress-text]');
      if (text) {
        if (cartCents < goal1Cents) {
          const remaining = customerCentsToDecimal(goal1Cents - cartCents);
          text.innerHTML = '<strong>' + formatCustomerMoney(remaining) + '</strong> away from free shipping';
        } else if (cartCents < goal2Cents) {
          const remaining = customerCentsToDecimal(goal2Cents - cartCents);
          text.innerHTML = "You’ve got free shipping! <strong>" + formatCustomerMoney(remaining) + '</strong> away from free gift';
        } else {
          text.innerHTML = "You’ve got free shipping &amp; a free gift!";
        }
      }

      // ----- Goal labels (the small label under the icons) -----
      const label1 = bar.querySelector('[data-goal-label="1"]');
      const label2 = bar.querySelector('[data-goal-label="2"]');
      if (label1) label1.textContent = formatCustomerMoney(customerCentsToDecimal(goal1Cents));
      if (label2) label2.textContent = formatCustomerMoney(customerCentsToDecimal(goal2Cents));
    }

    // ----- Free-gift wrapper (the "Choose your free gift" slider) -----
    const giftWrapper = document.querySelector('.free-gift-wrapper');
    if (giftWrapper) {
      // Show only when the cart truly meets the free-gift threshold in the
      // customer's currency. Use visibility/display so we don't toggle it
      // back on after a click-driven hide from `checkGiftInCart`.
      if (cartCents >= goal2Cents) {
        if (giftWrapper.style.display === 'none' && !giftWrapper.dataset.hiddenByChoice) {
          giftWrapper.style.display = '';
        }
      } else {
        giftWrapper.style.display = 'none';
      }
    }
  }

  // Re-run after any cart change so the bar stays in sync.
  //
  // Triggers we listen for:
  //   1. `cart:refresh` — fired from the theme's own cart-update fetch shim.
  //   2. Shopify's PUB_SUB cart-update event — fired by cart.js/product-form.js.
  //   3. CartDrawer.renderContents() — monkey-patched so we run AFTER it
  //      replaces innerHTML. (Inline <script> tags inside the new HTML are
  //      NOT executed by the browser when set via innerHTML — that's why the
  //      labels stayed in USD until the page was hard-refreshed.)
  //   4. A MutationObserver on the cart-drawer as a final safety net.
  //   5. DOMContentLoaded / window.load for the initial page render.

  function safeRefresh() {
    try { refreshCartFreeGift(); } catch (e) { /* noop */ }
  }

  document.addEventListener('cart:refresh', safeRefresh);
  document.addEventListener('DOMContentLoaded', safeRefresh);
  window.addEventListener('load', safeRefresh);

  // 2. Shopify's pub/sub system (cart.js, product-form.js use this).
  function subscribeToPubSub() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
        // data.cartData.total_price is the fresh cart total — pass it through
        // so we don't have to re-fetch /cart.js.
        const cart = data && data.cartData ? data.cartData : null;
        refreshCartFreeGift(cart);
      });
      return true;
    }
    return false;
  }
  if (!subscribeToPubSub()) {
    // pubsub.js may not have loaded yet — retry once after DOM ready.
    document.addEventListener('DOMContentLoaded', subscribeToPubSub);
  }

  // 3. Monkey-patch CartDrawer.renderContents so we fire AFTER its innerHTML
  //    swap. customElements.whenDefined waits until the class is registered.
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer').then(function () {
      const proto = customElements.get('cart-drawer') && customElements.get('cart-drawer').prototype;
      if (!proto || proto.__cartFreeGiftPatched) return;
      const orig = proto.renderContents;
      if (typeof orig !== 'function') return;
      proto.renderContents = function () {
        const r = orig.apply(this, arguments);
        // run after the section HTML has been swapped in
        Promise.resolve().then(safeRefresh);
        return r;
      };
      proto.__cartFreeGiftPatched = true;
      // Also refresh immediately for any drawer that's already on the page.
      safeRefresh();
    });
  }

  // 4. MutationObserver safety net — catches any other re-render path that
  //    might mutate the cart-drawer (e.g. cart-notification, theme sections
  //    refresh, app embeds replacing the drawer markup).
  function observeCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__cartFreeGiftObserved) return false;
    drawer.__cartFreeGiftObserved = true;
    const obs = new MutationObserver(function (mutations) {
      // Only react if the progress bar or gift wrapper appears/changes.
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        for (let j = 0; j < m.addedNodes.length; j++) {
          const n = m.addedNodes[j];
          if (n.nodeType !== 1) continue;
          if (
            n.matches && (n.matches('.cart-progress-bar') || n.matches('.free-gift-wrapper')) ||
            n.querySelector && (n.querySelector('.cart-progress-bar') || n.querySelector('.free-gift-wrapper'))
          ) {
            safeRefresh();
            return;
          }
        }
      }
    });
    obs.observe(drawer, { childList: true, subtree: true });
    return true;
  }
  if (!observeCartDrawer()) {
    document.addEventListener('DOMContentLoaded', observeCartDrawer);
  }

  // Expose for use from inline scripts that need the same conversion.
  window.cartFreeGift = {
    currencyInfo: currencyInfo,
    shopAmountToCustomerCents: shopAmountToCustomerCents,
    shippingGoalCents: shippingGoalCents,
    giftGoalCents: giftGoalCents,
    customerCentsToDecimal: customerCentsToDecimal,
    formatCustomerMoney: formatCustomerMoney,
    refresh: refreshCartFreeGift,
  };
})();
